<template>
  <div
    id="search-for-candidates"
    class="container"
  >
    <filters-modal
      :modal-is-active="filtersModalIsActive"
      @toggleModal="toggleFiltersModal"
      @updateLocations="updateLocations"
      @updateIndustries="updateIndustries"
      @updateSkills="updateSkills"
    />
    <section class="section has-nav-top-margin no-pad-bottom">
      <div id="search-bar">
        <div id="search-bar-title">
          <h4 class="title is-4 has-text-centered">
            <i class="fas fa-search">&nbsp;</i>
            Search for Candidates
          </h4>
          <div class="section is-paddingless has-text-centered has-text-grey">
            <p>Search by skills, industries, location, and more...</p>
          </div>
        </div>
        <form @submit.prevent="runSearch()">
          <div class="field has-addons">
            <p class="control is-expanded">
              <input
                v-model="searchObject.search"
                class="input"
                type="text"
                placeholder="Ex: Accounting, New York"
                @keyup="updateSearchObject()"
              >
            </p>
            <p class="control">
              <button
                type="submit"
                class="button is-rounded is-primary has-text-weight-bold"
              >
                Go
              </button>
            </p>
          </div>
        </form>
        <div class="filters-section">
          <p
            :class="[
              'control',
              'filters-icon',
              'has-text-weight-bold',
              'is-size-7',
            ]"
            @click="toggleFiltersModal"
          >
            <i class="fas fa-plus has-text-primary" />
            Add Filter
          </p>
          <div class="field" style="margin-left: auto;">
            <div class="control is-expanded">
              <div class="field is-inline">
                <input
                  id="searchFilters"
                  v-model="areSearchFiltersActive"
                  type="checkbox"
                  name="searchFilters"
                  class="switch is-outlined is-small"
                >
                <label for="searchFilters" />
              </div>
              <small class="is-size-7">Filters Enabled</small>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section
      v-if="chunkedResults.length > 0"
      id="search-results"
      class="section no-pad-top"
    >
      <div
        v-for="(row, rowIndex) in chunkedResults"
        :key="row.key"
        class="columns is-desktop expand"
      >
        <div
          v-for="(candidate, candidateIndex) in row"
          :key="candidate.key"
          class="column no-pad-bottom expand is-4-desktop is-12-mobile is-12-tablet"
        >
          <div class="card">
            <div class="card-content">
              <h4 class="title is-4">
                <strong>{{ candidate.username }}</strong>
              </h4>
              <p class="is-size-5 subtitle">
                {{ candidate.profession.name }}
                <span
                  class="is-size-7 has-text-grey"
                >({{ candidate.profession.years_experience }})</span>
              </p>
              <div
                v-if="candidate.location"
                id="candidate-location"
                class="subtitle"
              >
                <ul>
                  <li>
                    <i class="fas fa-map-marker-alt map-marker-icon fa-sm" />
                    <p
                      class="is-size-7 has-text-primary has-text-weight-bold is-inline"
                    >
                      &nbsp;{{ candidate.location.current }}
                    </p>
                  </li>
                </ul>
              </div>
              <div
                v-else
                id="candidate-location"
                class="subtitle"
              >
                <ul>
                  <li>
                    <i class="fas fa-map-marker-alt map-marker-icon fa-sm" />
                    <p
                      class="is-size-7 has-text-primary has-text-weight-bold is-inline"
                    >
                      &nbsp;Location Undisclosed
                    </p>
                  </li>
                </ul>
              </div>
              <div class="content">
                <div id="top-skills">
                  <span
                    v-for="skill in candidate.skills.slice(0, 3)"
                    :key="skill.key"
                    class="tag is-rounded is-primary"
                  >{{ skill }}</span>
                </div>
              </div>
            </div>
            <footer class="card-footer">
              <div class="card-footer-item">
                <button
                  class="button is-rounded is-primary is-small is-outlined has-text-weight-bold"
                  @click="viewCandidateProfile(chunkedResults[rowIndex][candidateIndex])"
                >
                  View Profile
                </button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </section>
    <section
      v-if="searchHasNoResults"
      id="no-results"
      class="section has-text-centered"
    >
      <p class="has-text-grey-light">
        No candidates match your search...
      </p>
    </section>
  </div>
</template>

<script>
import chunk from 'chunk';
import FiltersModal from '@/components/SearchForCandidates/FiltersModal';

export default {
  name: 'SearchForCandidates',
  components: {
    FiltersModal
  },
  data () {
    return {
      showSearchResults: false,
      searchObject: {},
      searchResults: [],
      filtersModalIsActive: false,
      searchHasNoResults: false
    };
  },
  computed: {
    chunkedResults () {
      return chunk(this.searchResults, 3);
    },
    areSearchFiltersActive: {
      get () {
        return this.$store.state.areSearchFiltersActive;
      },
      set () {
        this.$store.dispatch('handleToggleSearchFilters', !this.areSearchFiltersActive);
        this.runSearch();
      }
    },
    hasActiveFilters () {
      return (
        (
          this.searchObject.filters.location.length > 0
          || this.searchObject.filters.skills.length > 0
          || this.searchObject.filters.industries.length > 0
        ) && this.areSearchFiltersActive
      );
    }
  },
  created () {
    this.searchObject = Object.assign({}, this.$store.state.activeSearch);
    if (this.searchObject.search !== '') {
      this.runSearch();
    }
  },
  methods: {
    updateSearchObject () {
      this.$store.commit('UPDATE_SEARCH_OBJECT', { ...this.searchObject });
    },
    runSearch () {
      this.searchResults = [];
      this.$store
        .dispatch('searchForCandidates')
        .then(response => {
          let results = response.data.data;
          /* TODO -- Re-intruduce this filtering as needed.
          let filteredResults = results.filter(candidate =>
            this.verifyProfile(candidate)
          );
          this.searchResults = filteredResults; */
          this.searchResults = [...results];
          if (this.searchResults.length === 0) {
            this.searchHasNoResults = true;
          } else if (this.searchResults.length > 0) {
            this.searchHasNoResults = false;
          }
        })
        .catch(error => console.log(error));
    },
    toggleFiltersModal () {
      this.filtersModalIsActive = !this.filtersModalIsActive;
      this.runSearch();
    },
    viewCandidateProfile (candidateProfile) {
      this.$store.commit('SET_ACTIVE_CANDIDATE_PROFILE', candidateProfile);
      this.$router.push(`/profile/${candidateProfile._id}`);
    },
    updateLocations (locations) {
      this.searchObject.filters.location = [...locations];
      this.updateSearchObject();
    },
    updateIndustries (industries) {
      this.searchObject.filters.industries = [...industries];
      this.updateSearchObject();
    },
    updateSkills (skills) {
      this.searchObject.filters.skills = [...skills];
      this.updateSearchObject();
    },
    verifyProfile (candidate) {
      if (
        candidate.profession.name === ''
        || candidate.profession.years_experience === 0
      ) {
        return false;
      } else {
        return true;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.media-content {
  margin-bottom: 0.5rem;
}

#search-results {
  margin-top: 2rem;
}

#search-bar-title {
  margin-bottom: 2rem;
}

.skill-tag {
  margin: 0.25rem;
}

.is-stacked {
  flex-direction: column;
}

i {
  margin-left: 0.5rem;
  margin-bottom: 0.1rem;
}
.arrow {
  border: solid black;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
}

.down-arrow {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}

.dropdown-content {
  padding: 0.5rem;
}

.card {
  margin-bottom: 1rem;
}

@media only screen and (min-width: 769px) {
  #search-bar {
    max-width: 650px;
    margin: 0 auto;
  }
}

.tag {
  margin: 0.25rem;
}

.subtitle:not(:last-child) {
  margin-bottom: 0.5rem;
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  position: fixed;
}

.filters-section {
  margin-top: 0.5rem;
  display: flex;
}

.filters-icon {
  cursor: pointer;
}

.column.expand {
  display: inline-flex;
}

.column.expand > div {
  width: 100%; // only Edge needed this nonsense
}

.card-content {
  height: calc(100% - 60px);
}
</style>
